import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useProgramTypes } from "@/hooks/workflow/useProgramTypes";
import { useWorkflowsList } from "@/hooks/workflow/useWorkflowsList";
import { useTasksList } from "@/hooks/workflow/useTasksList";
import { useProgramTypeStatuses } from "@/hooks/workflow/useProgramTypeStatuses";
import { useAllProgramTypeStatuses } from "@/hooks/workflow/useAllProgramTypeStatuses";
import { useUserGroupsList } from "@/hooks/users/useUserGroupsList";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Search, Settings, Users, Clock, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { workflowService } from "@/services/features/WorkflowService";

const formSchema = z.object({
  workflowName: z.string().min(1, "Workflow Name is required"),
  applicablePrograms: z.array(z.string()).min(1, "At least one program must be selected"),
  triggeredByWorkflows: z.array(z.object({
    workflow: z.string(),
    status: z.array(z.string()),
  })).optional(),
  attempts: z.array(z.object({
    attemptNo: z.number(),
    tasks: z.string(),
    waitingPeriod: z.number().min(0, "Waiting period must be at least 0"),
    waitingPeriodUnit: z.enum(["Days", "Weeks", "Months"]),
  })).min(1, "At least one attempt is required"),
  userGroup: z.string().min(1, "User group is required"),
}).superRefine((data, ctx) => {
  // Validate that if a workflow is selected, status must also be selected
  data.triggeredByWorkflows?.forEach((trigger, index) => {
    if (trigger.workflow && trigger.status.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Status is required when workflow is selected",
        path: ["triggeredByWorkflows", index, "status"],
      });
    }
  });

  // Validate that if tasks are added, waiting period is required (must be >= 0)
  data.attempts.forEach((attempt, index) => {
    if (attempt.tasks && (attempt.waitingPeriod === null || attempt.waitingPeriod === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Waiting period is required when tasks are added",
        path: ["attempts", index, "waitingPeriod"],
      });
    }
  });
});

type FormData = z.infer<typeof formSchema>;

interface WorkflowFormProps {
  initialData?: {
    id?: string;
    workflowName?: string;
    applicablePrograms?: string[];
    triggeredByWorkflows?: Array<{
      workflow: string;
      status: string[];
    }>;
    attempts?: Array<{
      attemptNo: number;
      tasks: string;
      waitingPeriod: number;
      waitingPeriodUnit: "Days" | "Weeks" | "Months";
    }>;
    userGroup?: string;
  };
  isEdit?: boolean;
}

// Mock data
const availableUserTypes = ["CS", "VPE", "PES"];
const availableTasks = ["Task A", "Task B", "Task C", "Task D", "Task E", "Follow-up Call", "Email Reminder", "Document Review", "Assessment", "Consultation"];
const availableStatuses = ["Consent", "Deferred", "Escalate", "Decline", "Active", "Inactive", "Enrollment", "Pending", "Declined", "Paused", "Screening", "Assessment", "Treatment", "Follow-up", "Completed", "Scheduled", "In Progress", "No Show", "Cancelled"];

const programStatuses = {
  RPM: ["Consent", "Deferred", "Escalate", "Decline", "Active", "Inactive"],
  CCM: ["Enrollment", "Pending", "Active", "Declined", "Paused"],
  BHI: ["Screening", "Assessment", "Treatment", "Follow-up", "Completed"],
  AWV: ["Scheduled", "In Progress", "Completed", "No Show", "Cancelled"],
};

export const WorkflowForm = ({ initialData, isEdit = false }: WorkflowFormProps) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { programTypes, loading: loadingPrograms } = useProgramTypes();
  const { workflows, loading: loadingWorkflows } = useWorkflowsList();
  const { tasks, loading: loadingTasks } = useTasksList();
  const { userGroups, loading: loadingUserGroups } = useUserGroupsList();
  // Use different hooks based on whether we're editing or creating
  const { programTypeStatuses: editStatuses, loading: loadingEditStatuses } = useProgramTypeStatuses(
    isEdit && initialData?.id ? parseInt(initialData.id) : undefined
  );
  const { programTypeStatuses: allStatuses, loading: loadingAllStatuses } = useAllProgramTypeStatuses();
  
  // Always use all statuses for proper display of existing selections
  const programTypeStatuses = allStatuses;
  const loadingStatuses = loadingAllStatuses;
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workflowName: initialData?.workflowName || "",
      applicablePrograms: initialData?.applicablePrograms || [],
      triggeredByWorkflows: initialData?.triggeredByWorkflows || [
        { workflow: "", status: [] }
      ],
      attempts: initialData?.attempts || [
        { attemptNo: 1, tasks: "", waitingPeriod: 0, waitingPeriodUnit: "Days" }
      ],
      userGroup: initialData?.userGroup || "",
    },
  });

  const { fields: attemptFields, append: appendAttempt, remove: removeAttempt } = useFieldArray({
    control: form.control,
    name: "attempts",
  });

  const { fields: triggerFields, append: appendTrigger, remove: removeTrigger } = useFieldArray({
    control: form.control,
    name: "triggeredByWorkflows",
  });

  const selectedPrograms = form.watch("applicablePrograms");

  // Watch all form values to detect changes
  const formValues = form.watch();

  // Track changes for edit mode
  useEffect(() => {
    if (isEdit && initialData) {
      const hasFormChanges = 
        JSON.stringify(formValues.workflowName) !== JSON.stringify(initialData.workflowName) ||
        JSON.stringify(formValues.applicablePrograms?.sort()) !== JSON.stringify(initialData.applicablePrograms?.sort()) ||
        JSON.stringify(formValues.triggeredByWorkflows) !== JSON.stringify(initialData.triggeredByWorkflows) ||
        JSON.stringify(formValues.attempts) !== JSON.stringify(initialData.attempts) ||
        JSON.stringify(formValues.userGroup) !== JSON.stringify(initialData.userGroup);
      
      setHasChanges(hasFormChanges);
    }
  }, [formValues, initialData, isEdit]);

  // Group statuses by program type
  const groupedStatuses = programTypeStatuses.reduce((acc, item) => {
    if (!acc[item.program_type]) {
      acc[item.program_type] = [];
    }
    acc[item.program_type].push(item);
    return acc;
  }, {} as Record<string, typeof programTypeStatuses>);

  const convertTimeUnitToDays = (value: number, unit: "Days" | "Weeks" | "Months"): number => {
    switch (unit) {
      case "Days":
        return value;
      case "Weeks":
        return value * 7;
      case "Months":
        return value * 30;
      default:
        return value;
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!user || !token) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform program types to the required format
      const programTypesPayload = data.applicablePrograms.map(programName => {
        const programType = programTypes.find(p => p.program_type === programName);
        const programTypeId = programType?.program_type_id;
        return { programTypeId: typeof programTypeId === 'string' ? parseInt(programTypeId, 10) : (programTypeId || 0) };
      });

      // Transform triggers (workflow triggered by)
      // Each workflow trigger can have multiple statuses, so we need to create a trigger for each status
      // Ensure no duplicate programStatusMapperIds are sent
      const triggersPayload = data.triggeredByWorkflows.flatMap(trigger => {
        const workflow = workflows.find(w => w.workflow_name === trigger.workflow);
        const prevWorkflowId = workflow?.workflow_id || 0;
        
        // Remove duplicate status IDs before creating trigger objects
        const uniqueStatusIds = [...new Set(trigger.status)];
        
        // Create a trigger object for each unique selected status
        return uniqueStatusIds.map(statusId => ({
          prevWorkflowId,
          programStatusMapperId: parseInt(statusId, 10)
        }));
      });

      // Transform attempts with time conversion
      const attemptsPayload = data.attempts.map((attempt, index) => {
        const task = tasks.find(t => t.task_name === attempt.tasks);
        const waitTimeInDays = convertTimeUnitToDays(attempt.waitingPeriod, attempt.waitingPeriodUnit);
        
        return {
          taskId: task?.task_id || 0,
          attempt: index + 1,
          waitTime: waitTimeInDays
        };
      });

      // Get user group ID from the selected user group
      const selectedUserGroup = userGroups.find(g => g.user_group_id.toString() === data.userGroup);
      const userGroupId = selectedUserGroup?.user_group_id || parseInt(data.userGroup, 10);

      const workflowPayload = {
        workflowName: data.workflowName,
        programTypes: programTypesPayload,
        triggers: triggersPayload,
        attempts: attemptsPayload,
        userGroupId: userGroupId
      };

      console.log('Creating workflow with payload:', workflowPayload);

      if (isEdit && initialData?.id) {
        await workflowService.updateWorkflow(user.id.toString(), initialData.id, workflowPayload, token);
        
        toast({
          title: "Workflow Updated",
          description: `Workflow "${data.workflowName}" has been updated successfully.`,
        });
      } else {
        await workflowService.createWorkflow(user.id, workflowPayload, token);

        toast({
          title: "Workflow Created",
          description: `Workflow "${data.workflowName}" has been created successfully.`,
        });
      }

      navigate("/admin/workflow");
    } catch (error: any) {
      console.error('Error creating workflow:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create workflow",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    navigate("/admin/workflow");
  };

  const addAttempt = () => {
    const newAttemptNo = attemptFields.length + 1;
    appendAttempt({
      attemptNo: newAttemptNo,
      tasks: "",
      waitingPeriod: 0,
      waitingPeriodUnit: "Days",
    });
  };

  const addTrigger = () => {
    appendTrigger({
      workflow: "",
      status: [],
    });
  };

  // Get all selected workflows to filter them out from other dropdowns
  const allSelectedWorkflows = form.watch("triggeredByWorkflows").map(t => t.workflow).filter(Boolean);
  
  // Map workflows to workflow names for easier filtering
  const workflowNames = workflows.map(w => w.workflow_name);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  {isEdit ? "Edit Workflow" : "Create New Workflow"}
                </h1>
                <p className="text-muted-foreground">
                  {isEdit ? "Update your workflow configuration" : "Design a comprehensive workflow for your programs"}
                </p>
              </div>
            </div>
          </div>

          {/* Main Form Card */}
          <Card>
            <CardContent className="p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  {/* Basic Information Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                      Basic Information
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="workflowName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium">Workflow Name</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter a descriptive workflow name..."
                              className="bg-white border focus:border-primary/50 transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Applicable Programs */}
                    <FormField
                      control={form.control}
                      name="applicablePrograms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium">Applicable Programs</FormLabel>
                          <FormControl>
                            <Popover>
                                  <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    "w-full justify-between",
                                    field.value.length === 0 && "text-muted-foreground"
                                  )}
                                  disabled={loadingPrograms}
                                >
                                  {field.value.length === 0 ? (
                                    loadingPrograms ? "Loading programs..." : "Select applicable programs..."
                                  ) : (
                                    <div className="flex flex-wrap gap-1 max-w-full">
                                      {field.value.slice(0, 2).map((program) => (
                                        <Badge key={program} variant="secondary" className="text-xs">
                                          {program}
                                        </Badge>
                                      ))}
                                      {field.value.length > 2 && (
                                        <Badge variant="secondary" className="text-xs">
                                          +{field.value.length - 2} more
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                                <Command className="bg-background">
                                  <CommandInput placeholder="Search programs..." className="border-none" />
                                  <CommandList className="max-h-60">
                                    <CommandEmpty className="p-4 text-sm text-muted-foreground">No program found.</CommandEmpty>
                                    <CommandGroup>
                                      {programTypes.map((program) => (
                                        <CommandItem
                                          value={program.program_type}
                                          key={program.program_type_id}
                                          onSelect={() => {
                                            if (field.value.includes(program.program_type)) {
                                              field.onChange(field.value.filter((p) => p !== program.program_type));
                                            } else {
                                              field.onChange([...field.value, program.program_type]);
                                            }
                                          }}
                                          className="cursor-pointer hover:bg-muted"
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              field.value.includes(program.program_type) ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {program.program_type}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Workflow Triggered By Section */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        Workflow triggered by
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg border overflow-visible">
                      <table className="w-full table-fixed">
                        {/* Header Row */}
                        <thead>
                          <tr className="bg-muted/30 border-b">
                            <th className="text-left p-4 font-medium text-muted-foreground w-1/2">Workflow</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-1/2">Status</th>
                            <th className="text-center p-4 font-medium text-muted-foreground w-20">Action</th>
                          </tr>
                        </thead>
                        
                        {/* Data Rows */}
                        <tbody>
                          {triggerFields.map((field, index) => (
                            <tr key={field.id} className={`hover:bg-muted/10 transition-colors ${index !== triggerFields.length - 1 ? 'border-b' : ''}`}>
                              {/* Workflow Dropdown */}
                              <td className="p-4">
                                <FormField
                                  control={form.control}
                                  name={`triggeredByWorkflows.${index}.workflow`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                         <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="outline"
                                              role="combobox"
                                              disabled={loadingWorkflows}
                                              className={cn(
                                                "w-full justify-between",
                                                !field.value && "text-muted-foreground"
                                              )}
                                            >
                                              {loadingWorkflows ? (
                                                "Loading workflows..."
                                              ) : field.value ? (
                                                workflows.find((w) => w.workflow_name === field.value)?.workflow_name || field.value
                                              ) : (
                                                "Select workflow..."
                                              )}
                                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                                            <Command className="bg-background">
                                              <CommandInput placeholder="Search workflows..." className="border-none" />
                                              <CommandList className="max-h-60">
                                                <CommandEmpty className="p-4 text-sm text-muted-foreground">
                                                  {loadingWorkflows ? "Loading..." : "No workflow found."}
                                                </CommandEmpty>
                                                 <CommandGroup>
                                                   {workflows
                                                     .filter(workflow => 
                                                       !allSelectedWorkflows.includes(workflow.workflow_name) || workflow.workflow_name === field.value
                                                     )
                                                     .map((workflow) => (
                                                     <CommandItem
                                                       value={workflow.workflow_name}
                                                       key={workflow.workflow_id}
                                                       onSelect={() => {
                                                         field.onChange(workflow.workflow_name);
                                                       }}
                                                       className="cursor-pointer hover:bg-muted"
                                                     >
                                                       <Check
                                                         className={cn(
                                                           "mr-2 h-4 w-4",
                                                           workflow.workflow_name === field.value ? "opacity-100" : "opacity-0"
                                                         )}
                                                       />
                                                       {workflow.workflow_name}
                                                     </CommandItem>
                                                   ))}
                                                 </CommandGroup>
                                              </CommandList>
                                            </Command>
                                          </PopoverContent>
                                        </Popover>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </td>
                              
                               {/* Status Dropdown - Multi Select */}
                               <td className="p-4">
                                 <FormField
                                   control={form.control}
                                   name={`triggeredByWorkflows.${index}.status`}
                                   render={({ field }) => (
                                     <FormItem>
                                       <FormControl>
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <Button
                                                variant="outline"
                                                role="combobox"
                                                disabled={loadingStatuses}
                                                className={cn(
                                                  "w-full justify-between min-h-[60px] h-auto py-3",
                                                  field.value.length === 0 && "text-muted-foreground"
                                                )}
                                              >
                                                <div className="flex-1 text-left">
                                                  {loadingStatuses ? (
                                                   "Loading statuses..."
                                                 ) : field.value.length === 0 ? (
                                                   "Select statuses..."
                                                  ) : (
                                                    <div className="flex flex-wrap gap-1.5">
                                                      {field.value.map((statusId) => {
                                                        // Find the status item by ID
                                                        const statusItem = programTypeStatuses.find(
                                                          s => s.program_status_mapper_id.toString() === statusId
                                                        );
                                                        return (
                                                          <Badge key={statusId} variant="secondary" className="text-xs px-2 py-1">
                                                            {statusItem ? statusItem.program_status : `Status ID: ${statusId}`}
                                                          </Badge>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 self-start mt-1" />
                                              </Button>
                                            </PopoverTrigger>
                                           <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                                             <Command className="bg-background">
                                               <CommandInput placeholder="Search statuses..." className="border-none" />
                                               <CommandList className="max-h-60">
                                                 <CommandEmpty className="p-4 text-sm text-muted-foreground">
                                                   {loadingStatuses ? "Loading..." : "No status found."}
                                                 </CommandEmpty>
                                                    {Object.entries(groupedStatuses).map(([programType, statuses]) => (
                                                      <CommandGroup key={programType} heading={programType}>
                                                        {statuses.map((statusItem) => {
                                                          const statusId = statusItem.program_status_mapper_id.toString();
                                                          const isSelected = field.value.includes(statusId);
                                                          
                                                          return (
                                                            <CommandItem
                                                              value={`${programType}-${statusItem.program_status}`}
                                                              key={statusItem.program_status_mapper_id}
                                                              onSelect={() => {
                                                                if (isSelected) {
                                                                  // Remove the status
                                                                  field.onChange(field.value.filter((s) => s !== statusId));
                                                                } else {
                                                                  // Add the status ID, ensuring no duplicates using Set
                                                                  const newValue = [...new Set([...field.value, statusId])];
                                                                  field.onChange(newValue);
                                                                }
                                                              }}
                                                              className="cursor-pointer hover:bg-muted"
                                                            >
                                                              <Check
                                                                className={cn(
                                                                  "mr-2 h-4 w-4",
                                                                  isSelected ? "opacity-100" : "opacity-0"
                                                                )}
                                                              />
                                                              {statusItem.program_status}
                                                            </CommandItem>
                                                          );
                                                        })}
                                                      </CommandGroup>
                                                    ))}
                                               </CommandList>
                                             </Command>
                                           </PopoverContent>
                                         </Popover>
                                       </FormControl>
                                       <FormMessage />
                                     </FormItem>
                                   )}
                                 />
                               </td>
                              
                              {/* Delete Button */}
                              <td className="p-4 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive transition-all"
                                   onClick={() => {
                                     removeTrigger(index);
                                     // If this was the last trigger, add a new blank one
                                     if (triggerFields.length === 1) {
                                       appendTrigger({ workflow: "", status: [] });
                                     }
                                   }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="p-4 pt-0">
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="transition-all px-0"
                          onClick={addTrigger}
                        >
                          <Plus className="w-4 h-4" />
                          Add Trigger
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Attempts Section */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        Attempt Configuration
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg border">
                      <table className="w-full">
                        {/* Header Row */}
                        <thead>
                          <tr className="bg-muted/30 border-b">
                            <th className="text-center p-4 font-medium text-muted-foreground w-32">Attempt No.</th>
                            <th className="text-left p-4 font-medium text-muted-foreground">Tasks</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-40">Waiting Period</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-28">Unit</th>
                            <th className="text-center p-4 font-medium text-muted-foreground w-20">Action</th>
                          </tr>
                        </thead>
                        
                        {/* Data Rows */}
                        <tbody>
                          {attemptFields.map((field, index) => (
                            <tr key={field.id} className={`hover:bg-muted/10 transition-colors ${index !== attemptFields.length - 1 ? "border-b" : ""}`}>
                              {/* Attempt Number - Display Only */}
                              <td className="p-4 text-center">
                                <div className="p-2 bg-primary/10 rounded-md flex items-center justify-center">
                                  <span className="font-bold text-primary text-sm">Attempt {index + 1}</span>
                                </div>
                              </td>
                              
                              {/* Tasks - Searchable Dropdown */}
                              <td className="p-4">
                                <FormField
                                  control={form.control}
                                  name={`attempts.${index}.tasks`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="outline"
                                              role="combobox"
                                              disabled={loadingTasks}
                                              className={cn(
                                                "w-full justify-between",
                                                !field.value && "text-muted-foreground"
                                              )}
                                            >
                                              {loadingTasks ? (
                                                "Loading tasks..."
                                              ) : field.value ? (
                                                tasks.find((task) => task.task_name === field.value)?.task_name || field.value
                                              ) : (
                                                "Select task..."
                                              )}
                                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                                            <Command className="bg-background">
                                              <CommandInput placeholder="Search tasks..." className="border-none" />
                                              <CommandList className="max-h-60">
                                                <CommandEmpty className="p-4 text-sm text-muted-foreground">
                                                  {loadingTasks ? "Loading..." : "No task found."}
                                                </CommandEmpty>
                                                <CommandGroup>
                                                  {tasks.map((task) => (
                                                    <CommandItem
                                                      value={task.task_name}
                                                      key={task.task_id}
                                                      onSelect={() => {
                                                        field.onChange(task.task_name);
                                                      }}
                                                      className="cursor-pointer hover:bg-muted"
                                                    >
                                                      <Check
                                                        className={cn(
                                                          "mr-2 h-4 w-4",
                                                          task.task_name === field.value ? "opacity-100" : "opacity-0"
                                                        )}
                                                      />
                                                      {task.task_name}
                                                    </CommandItem>
                                                  ))}
                                                </CommandGroup>
                                              </CommandList>
                                            </Command>
                                          </PopoverContent>
                                        </Popover>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </td>
                              
                              {/* Waiting Period */}
                              <td className="p-4">
                                <FormField
                                  control={form.control}
                                  name={`attempts.${index}.waitingPeriod`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Input
                                          type="number"
                                          min="0"
                                          placeholder="0"
                                          className="h-10 bg-white border focus:border-primary/50 transition-colors"
                                          {...field}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            field.onChange(value === '' ? 0 : parseInt(value));
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </td>
                              
                              {/* Waiting Period Unit */}
                              <td className="p-4">
                                <FormField
                                  control={form.control}
                                  name={`attempts.${index}.waitingPeriodUnit`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                          <SelectTrigger className="h-10 bg-white border focus:border-primary/50">
                                            <SelectValue placeholder="Unit" />
                                          </SelectTrigger>
                                          <SelectContent className="bg-white">
                                            <SelectItem value="Days">Days</SelectItem>
                                            <SelectItem value="Weeks">Weeks</SelectItem>
                                            <SelectItem value="Months">Months</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </td>
                              
                              {/* Delete Button */}
                              <td className="p-4 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive transition-all"
                                  onClick={() => {
                                    removeAttempt(index);
                                    // If this was the last attempt, add a new blank one
                                    if (attemptFields.length === 1) {
                                      appendAttempt({
                                        attemptNo: 1,
                                        tasks: "",
                                        waitingPeriod: 0,
                                        waitingPeriodUnit: "Days",
                                      });
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="p-4 pt-0">
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="transition-all px-0"
                          onClick={addAttempt}
                        >
                          <Plus className="w-4 h-4" />
                          Add Attempt
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* User Group Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                      Workflow Ownership
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="userGroup"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium">
                            User Group <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  disabled={loadingUserGroups}
                                  className={cn(
                                    "w-full justify-between bg-white border focus:border-primary/50",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {loadingUserGroups ? (
                                    "Loading user groups..."
                                  ) : field.value ? (
                                    userGroups.find((group) => group.user_group_id.toString() === field.value)?.user_group_name || "Select user group..."
                                  ) : (
                                    "Select the user group that can be assigned to this workflow..."
                                  )}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md" align="start">
                                <Command className="bg-background">
                                  <CommandInput placeholder="Search user groups..." className="border-none" />
                                  <CommandList className="max-h-60">
                                    <CommandEmpty className="p-4 text-sm text-muted-foreground">
                                      {loadingUserGroups ? "Loading..." : "No user group found."}
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {userGroups.map((group) => (
                                        <CommandItem
                                          value={group.user_group_name}
                                          key={group.user_group_id}
                                          onSelect={() => {
                                            field.onChange(group.user_group_id.toString());
                                          }}
                                          className="cursor-pointer hover:bg-muted"
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              group.user_group_id.toString() === field.value ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {group.user_group_name}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage className="text-destructive" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end items-center gap-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={onCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      variant="default"
                      disabled={isSubmitting || (isEdit && !hasChanges)}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {isEdit ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        <>{isEdit ? "Update" : "Create"} Workflow</>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};